import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent } from '@nucleus-commerce/admin-kit/layout';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { VariantEditor } from '../components/variant-editor';
import { VARIANTS_SECTION_ID } from '../components/variants-section';
import { useProduct } from '../hooks/use-product';
import { useDeleteVariant } from '../hooks/use-variant-mutations';
import { useVariants } from '../hooks/use-variants';

const ROUTE = '/_authed/_active/org/products/$productId_/variants/$variantId' as const;
export const VARIANT_PAGE_PATH = '/org/products/$productId/variants/$variantId' as const;

function VariantPage() {
  const { t } = useTranslation('products');
  const { productId, variantId } = useParams({ from: ROUTE });
  const navigate = useNavigate();
  const product = useProduct(productId);
  const variants = useVariants(productId);
  const remove = useDeleteVariant(productId);
  const linkTo = useCallback(
    (id: string) => ({ to: VARIANT_PAGE_PATH, params: { productId, variantId: id } }),
    [productId],
  );

  const loading = (
    <PageContent>
      <Skeleton className="h-10 w-72" />
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <Skeleton className="h-80" />
        <Skeleton className="h-[32rem]" />
      </div>
    </PageContent>
  );
  if (product.isPending || variants.isPending) return loading;
  const error = product.error ?? variants.error;
  if (error || !product.data || !variants.data) {
    return <ErrorState error={error} onRetry={() => void variants.refetch()} />;
  }
  const variant = variants.data.find((v) => v.id === variantId);
  if (!variant) {
    // Deleting it takes the row out of the cache before the page has left.
    if (remove.isPending || remove.isSuccess) return loading;
    return (
      <PageContent>
        <p className="text-muted-foreground py-10 text-center text-sm">
          {t('variantPage.notFound')}
        </p>
      </PageContent>
    );
  }
  const back = {
    to: '/org/products/$productId',
    params: { productId },
    hash: VARIANTS_SECTION_ID,
  } as const;
  return (
    <VariantEditor
      key={variant.id}
      product={product.data}
      variant={variant}
      variants={variants.data}
      backTo={back}
      backLabel={product.data.title}
      linkTo={linkTo}
      onDelete={(id) =>
        remove.mutate(id, {
          onSuccess: () => {
            // The variant is gone, so unsaved edits to it must not hold the person here.
            releaseSaveBar();
            void navigate(back);
          },
        })
      }
    />
  );
}

/** `/org/products/$productId/variants/$variantId`: one variant on its own page (ADR 97). */
export function VariantEditPage() {
  return (
    <FeatureErrorBoundary name="products.variant">
      <VariantPage />
    </FeatureErrorBoundary>
  );
}
